import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_665a114b-bc62-4112-9e11-8ccdfcfc0d6e/artifacts/auplmu5t_1774296304598.png';
const SITE_URL = process.env.REACT_APP_BACKEND_URL || 'https://vorrei-preview.emergent.host';

export const ShareSiteButton = ({ className = '' }) => {
  const { language } = useLanguage();

  const handleShare = () => {
    const text = language === 'it'
      ? 'Scopri Moneyfor - la piattaforma che connette chi può aiutare con chi ha bisogno'
      : 'Discover Moneyfor - the platform that connects those who can help with those in need';
    
    if (navigator.share) {
      navigator.share({ title: 'Moneyfor', text, url: SITE_URL }).catch(() => {});
    } else {
      navigator.clipboard.writeText(SITE_URL);
      toast.success(language === 'it' ? 'Link copiato!' : 'Link copied!');
    }
  };

  return (
    <button onClick={handleShare} data-testid="share-site-btn"
      className={`inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-colors text-sm font-medium ${className}`}>
      <Share2 className="h-4 w-4" />
      {language === 'it' ? 'Condividi Moneyfor' : 'Share Moneyfor'}
    </button>
  );
};

const Footer = () => {
  const { language } = useLanguage();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(SITE_URL);
    toast.success(language === 'it' ? 'Link copiato!' : 'Link copied!');
  };

  return (
    <footer className="bg-stone-900 text-stone-400" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <img src={LOGO_URL} alt="Moneyfor" className="h-8 w-8" style={{ mixBlendMode: 'multiply', filter: 'brightness(1.3)' }} />
              <span className="font-heading text-lg font-bold text-white">Moneyfor</span>
            </div>
            <p className="text-sm leading-relaxed">
              {language === 'it'
                ? 'Connetti chi può aiutare con chi ha bisogno.'
                : 'Connect those who can help with those in need.'}
            </p>
            {/* Share Site Link */}
            <button onClick={handleCopyLink} data-testid="footer-copy-link"
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors">
              <Copy className="h-3 w-3" /> moneyfor.it
            </button>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">
              {language === 'it' ? 'Piattaforma' : 'Platform'}
            </h4>
            <div className="space-y-2 text-sm">
              <Link to="/feed" className="block hover:text-white transition-colors">{language === 'it' ? 'Richieste' : 'Requests'}</Link>
              <Link to="/leaderboard" className="block hover:text-white transition-colors">{language === 'it' ? 'Classifica' : 'Leaderboard'}</Link>
              <Link to="/corporate-fund" className="block hover:text-white transition-colors">{language === 'it' ? 'Fondo Aziendale' : 'Corporate Fund'}</Link>
              <Link to="/marketplace" className="block hover:text-white transition-colors">{language === 'it' ? 'Mercatino Solidale' : 'Marketplace'}</Link>
              <Link to="/donate-time" className="block hover:text-white transition-colors">{language === 'it' ? 'Dona il tuo Tempo' : 'Donate Time'}</Link>
              <Link to="/success-stories" className="block hover:text-white transition-colors">{language === 'it' ? 'Storie di Successo' : 'Success Stories'}</Link>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-3">
              {language === 'it' ? 'Aziende' : 'Business'}
            </h4>
            <div className="space-y-2 text-sm">
              <Link to="/advertise" className="block hover:text-white transition-colors">{language === 'it' ? 'Pubblicità' : 'Advertise'}</Link>
              <Link to="/corporate-fund" className="block hover:text-white transition-colors">Partnership</Link>
              <a href="mailto:postmaster@moneyfor.it" className="block hover:text-white transition-colors">postmaster@moneyfor.it</a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-3">
              {language === 'it' ? 'Supporto' : 'Support'}
            </h4>
            <div className="space-y-2 text-sm">
              <Link to="/contact" className="block hover:text-white transition-colors">{language === 'it' ? 'Contatti' : 'Contact'}</Link>
              <Link to="/terms" className="block hover:text-white transition-colors">{language === 'it' ? 'Termini' : 'Terms'}</Link>
              <Link to="/privacy" className="block hover:text-white transition-colors">{language === 'it' ? 'Privacy e Dati' : 'Privacy'}</Link>
              <a href="mailto:postmaster@moneyfor.it" className="block hover:text-white transition-colors">postmaster@moneyfor.it</a>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs">
            © {new Date().getFullYear()} Moneyfor. {language === 'it' ? 'Tutti i diritti riservati.' : 'All rights reserved.'}
          </p>
          <div className="flex items-center gap-1 text-xs">
            <span>{language === 'it' ? 'Fatto con' : 'Made with'}</span>
            <Heart className="h-3 w-3 text-red-500" fill="currentColor" />
            <span>{language === 'it' ? 'per un mondo migliore' : 'for a better world'}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
